'use client';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import TripCard from '@/components/trip-card/trip-card';
import { api, type RouterOutputs } from '@/server/callers/client';

interface TripsFiltersProps {
  trips: RouterOutputs['trips']['list'];
  destinations: RouterOutputs['destinations']['list'];
}

export default function TripsFilters({
  trips: initialTrips,
  destinations,
}: TripsFiltersProps) {
  const [selectedDestination, setSelectedDestination] = useState<string>(
    destinations[0]?.name || '',
  );
  const trips = api.trips.list.useQuery(
    {
      pagination: {
        page: 1,
        pageSize: 6,
      },
      sorting: {
        column: 'createdAt',
        direction: 'desc',
      },
      filters: { destinationName: selectedDestination },
    },
    {
      initialData: initialTrips,
    },
  );

  useEffect(() => {
    trips.refetch();
  }, [selectedDestination]);

  return (
    <section data-aos="fade-up" className="py-20">
      <div className="max-w-4xl mx-auto text-center space-y-4">
        <h2 className="font-outfit text-4xl md:text-6xl font-medium leading-[120%] tracking-[-1.5px] text-center text-[#161618]">
          Explore Exotic Destinations
        </h2>
        <p className="font-outfit text-lg font-light leading-[1.64em] tracking-[-0.2px] text-center text-[#56575c]">
          Embark on a journey to far-flung corners of the globe...
        </p>
      </div>
      {/* Filter Buttons */}
      <div className="flex flex-wrap justify-center gap-3 mt-10">
        {destinations.map((destination) => (
          <button
            key={destination.id}
            onClick={() => setSelectedDestination(destination.name)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              selectedDestination === destination.name
                ? 'bg-black text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
            type="button"
          >
            {destination.name}
          </button>
        ))}
      </div>

      {/* Destination Cards Grid */}
      <div className="max-w-[1280px] mx-auto px-4 pt-10">
        <div className="flex items-center flex-wrap justify-center gap-6">
          {trips.data?.map((trip) => (
            <TripCard
              key={trip.id}
              className="w-full md:max-w-[40%] lg:max-w-[30%] h-[450px]"
              trip={{
                id: Number(trip.id),
                name: trip.name,
                image: trip.cover?.key,
                duration: `${trip.duration} Days`,
                maxPeople: trip.groupSize,
                destination:
                  destinations.find(
                    (destination) => destination.id === trip.destinationId,
                  )?.name || '',
                destinationSlug:
                  destinations.find(
                    (destination) => destination.id === trip.destinationId,
                  )?.slug || '',
                overview: trip.overview,
                slug: trip.slug,
              }}
            />
          ))}
          <div className="w-full md:max-w-[40%] lg:max-w-[30%] h-auto flex items-center justify-center">
            <Link
              href={`/destinations/${destinations.find((destination) => destination.name === selectedDestination)?.slug}`}
              className="mt-10 text-black font-outfit text-lg font-medium leading-[1.64em] tracking-[-0.2px] bg-white rounded-full px-4 py-2 shadow-md hover:bg-secondary"
            >
              See More
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
